// apps/client/src/pages/salaries/Salaries.tsx
import {
  ProjectSalaryEmployeesResponse,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
  XeroSyncPayslipsResponse,
} from '@avanzu/types';
import {
  CheckCircle,
  EditOutlined,
  GroupOutlined,
  PersonOutlined,
  RequestQuoteOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import { fetchProjectActivities } from '../../services/projects';
import formatCurrency from '../../utils/formatCurrency';
import PersonPercentIcon from './components/PersonPercentIcon';
import XeroSyncPayslips from './components/XeroSyncPayslips';

export const SalariesPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

interface EmployeeSalaryData {
  id: string;
  name: string;
  totalWages: number;
  totalSuper: number;
  projectAllocation: number;
  allocations: {
    activityId: string;
    wages: number;
    super: number;
  }[];
}

interface projectDates {
  startDate: Date;
  endDate: Date;
}

export const Salaries: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeSalaryData[]>([]);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [projectDates, setProjectDates] = useState<projectDates | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<XeroSyncPayslipsResponse | null>(null);

  const navigate = useNavigate();

  const { currentOrganisation, organisationId } = useAuth();
  const { currentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();
  const projectId = currentProject?.id;

  useEffect(() => {
    if (organisationId && projectId) {
      loadData();
    }
  }, [organisationId, projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch activities and salary data in parallel
      const [activitiesData, salaryResponse] = await Promise.all([
        fetchProjectActivities(organisationId!, projectId!),
        apiClient.get(`/organisations/${organisationId}/projects/${projectId}/salaries/employees`),
      ]);

      if (!salaryResponse.ok) {
        throw new Error('Failed to fetch salary data');
      }

      const salaryData: ProjectSalaryEmployeesResponse = await salaryResponse.json();

      if (salaryData.success) {
        setActivities(activitiesData.activities);
        setEmployees(salaryData.employees);

        setProjectDates({
          startDate: new Date(
            Number(currentProject?.taxYear.substring(0, 4)),
            Number(currentOrganisation?.taxYearStartMonth) - 1,
            1
          ),
          endDate: new Date(
            Number(currentProject?.taxYear.substring(0, 4)) + 1,
            Number(currentOrganisation?.taxYearStartMonth) - 1,
            0 // previous month day 0 = last day of previous month
          ),
        });
      } else {
        setError(salaryData.message || 'Failed to load salary data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading salary data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncComplete = (result: XeroSyncPayslipsResponse) => {
    setSyncResult(result);
    // Reload data after successful sync
    if (result.success) {
      enqueueSnackbar(`Successfully synced ${result.processed} payslips`, {
        variant: 'success',
      });
      loadData();
    }
  };

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  const getEmployeeAllocation = (employee: EmployeeSalaryData, activityId: string) => {
    return employee.allocations.find((a) => a.activityId === activityId);
  };

  const getActivityTotal = (activityId: string, type: 'wages' | 'super') => {
    return employees.reduce((total, employee) => {
      const allocation = getEmployeeAllocation(employee, activityId);
      return total + (allocation ? allocation[type] : 0);
    }, 0);
  };

  if (!organisationId || !projectId || !currentProject || !currentOrganisation) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<RequestQuoteOutlined />}
          title="No project selected"
          message="Please select a project to view salary allocations."
        />
      </ContentWrapper>
    );
  }

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="Employee Salaries">
          <LoadingComponent context="Loading salary data..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (error) {
    return (
      <ContentWrapper>
        <PaperSection title="Employee Salaries">
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<RequestQuoteOutlined />}
              title="Error loading salary data"
              message={error}
              showBorder={false}
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Employee Salaries - ${currentProject?.name}`}
        action={
          <>
            <Button
              component={RouterLink}
              to={`/app/employees/${projectId}`}
              sx={{ color: 'primary.main' }}
            >
              Assign Employees
            </Button>
            <XeroSyncPayslips
              startDate={projectDates?.startDate}
              endDate={projectDates?.endDate}
              onSyncComplete={handleSyncComplete}
            />
          </>
        }
      >
        {syncResult?.success && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle color="success" sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="success.main" fontWeight={500}>
                Payslips synced successfully
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {syncResult.processed} processed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {syncResult.created} created
              </Typography>
              {syncResult.skipped > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {syncResult.skipped} skipped
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
        )}

        {employees.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<GroupOutlined />}
              title="No assigned employees"
              message="Employees need to be allocated to the project and have payslip data synced from Xero."
              button={
                <Button variant="contained" onClick={() => navigate(`/app/employees/${projectId}`)}>
                  Allocate your first employee
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No research activities"
              message="Create research activities first to view salary allocations."
              button={
                <Button
                  variant="contained"
                  onClick={() => navigate(`/app/activities/${projectId}`)}
                >
                  Create your first activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Box}
              sx={{
                mx: -6,
                px: 6,
                width: 'calc(100% + 96px)',
                // maxHeight: '70vh', // this forces vertical scroll in the panel
                // minHeight: '60vh',
                overflowX: 'scroll',
                '&::-webkit-scrollbar': {
                  WebkitAppearance: 'none',
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 0, 0, .3)',
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      zIndex: (theme) => theme.zIndex.appBar + 1,
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      borderTop: '0px none',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ width: 250, minWidth: 250, fontWeight: 600 }}>
                      Employee
                    </TableCell>
                    <TableCell
                      sx={{ width: 140, minWidth: 140, fontWeight: 600, textAlign: 'center' }}
                    >
                      Total Wages
                      <Typography variant="body2" color="text.secondary">
                        (Super)
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 120,
                        minWidth: 120,
                        fontWeight: 600,
                        textAlign: 'center',
                        backgroundColor: '#EFF2FE !important',
                      }}
                    >
                      Project %
                    </TableCell>
                    <TableCell
                      sx={{ width: 140, minWidth: 140, fontWeight: 600, textAlign: 'center' }}
                    >
                      Project Wages
                      <Typography variant="body2" color="text.secondary">
                        (Super)
                      </Typography>
                    </TableCell>
                    {sortedActivities.map((activity) => (
                      <TableCell
                        key={activity.id}
                        sx={{ width: 120, minWidth: 120, p: 1, verticalAlign: 'top' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                            size="small"
                            color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            align="center"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {activity.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 20,
                              height: 20,
                              bgcolor: 'primary.light',
                            }}
                          >
                            <PersonOutlined fontSize="small" sx={{ fontSize: 14 }} />
                          </Avatar>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {employee.name}
                            </Typography>
                            <Tooltip title="Edit employee allocation" arrow>
                              <IconButton
                                component={RouterLink}
                                to={`/app/employees/${projectId}/details/${employee.id}`}
                                size="small"
                                sx={{
                                  p: 0.25,
                                  color: 'text.secondary',
                                  '&:hover': { color: 'primary.main' },
                                }}
                              >
                                <EditOutlined sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.primary">
                          {formatCurrency(employee.totalWages)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(employee.totalSuper)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', bgcolor: '#f8f9ff' }}>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {employee.projectAllocation.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.primary">
                          {formatCurrency(employee.projectAllocation * employee.totalWages * 0.01)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(employee.projectAllocation * employee.totalSuper * 0.01)}
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const allocation = getEmployeeAllocation(employee, activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                            {allocation && (allocation.wages > 0 || allocation.super > 0) ? (
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {formatCurrency(allocation.wages)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatCurrency(allocation.super)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}

                  {/* Totals row */}
                  {employees.length > 1 && (
                    <TableRow
                      sx={{
                        bgcolor: 'grey.50',
                        '& .MuiTableCell-root': {
                          borderTop: '2px solid',
                          borderColor: 'divider',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          Total ({employees.length} employees)
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(employees.reduce((sum, emp) => sum + emp.totalWages, 0))}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {formatCurrency(employees.reduce((sum, emp) => sum + emp.totalSuper, 0))}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Percentage of total wages and super allocated to this project, used to allocate employee based overhead costs.">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <PersonPercentIcon sx={{ fontSize: '20px', color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {(() => {
                                const totalWages = employees.reduce(
                                  (sum, emp) => sum + emp.totalWages,
                                  0
                                );
                                const totalProjectWages = employees.reduce(
                                  (sum, emp) => sum + emp.projectAllocation * emp.totalWages * 0.01,
                                  0
                                );
                                const percentage =
                                  totalWages > 0 ? (totalProjectWages / totalWages) * 100 : 0;
                                return `${percentage.toFixed(1)}%`;
                              })()}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(
                            employees.reduce(
                              (sum, emp) => sum + emp.projectAllocation * emp.totalWages * 0.01,
                              0
                            )
                          )}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {formatCurrency(
                            employees.reduce(
                              (sum, emp) => sum + emp.projectAllocation * emp.totalSuper * 0.01,
                              0
                            )
                          )}
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const totalWages = getActivityTotal(activity.id, 'wages');
                        const totalSuper = getActivityTotal(activity.id, 'super');
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                            {totalWages > 0 || totalSuper > 0 ? (
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatCurrency(totalWages)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                >
                                  {formatCurrency(totalSuper)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {employees.length} employee{employees.length !== 1 ? 's' : ''} with salary
                data
              </Typography>
              {/* <Typography variant="body2" color="text.secondary">
                Amounts shown are for the current financial year
              </Typography> */}
            </Box>
          </>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Salaries;
